// src/components/AssignedVendors.js
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import API from "../services/api";
import {
  Row,
  Col,
  Button,
  Spinner,
  Badge,
  Modal,
} from "react-bootstrap";
import "./AdminVendorList.css";

/* ---------- helpers ---------- */
const fmtDate = (d) => (d ? new Date(d).toLocaleString() : "—");
const orDash = (v) =>
  v === 0 ? "0" : v == null || String(v).trim() === "" ? "—" : String(v);
const phoneLink = (p) => (p ? <a href={`tel:${p}`}>{p}</a> : "");
const mailLink = (m) => (m ? <a href={`mailto:${m}`}>{m}</a> : "");
const getInitials = (name = "") =>
  String(name || "")
    .split(" ")
    .map((part) => part.trim().charAt(0))
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase() || "AV";
const getAvatarTone = (seed = "") => {
  const tones = ["blue", "green", "amber", "violet", "rose", "sky"];
  const total = String(seed || "").split("").reduce((sum, ch) => sum + ch.charCodeAt(0), 0);
  return tones[total % tones.length];
};

/* Strict mapping you asked:
   - Company: vendor.companyName -> vendor.fullName -> ""
   - GST:     vendor.gstNumber  -> ""
   - Address: vendor.address    -> ""
*/
const getCompany = (v) =>
  v?.vendor?.companyName || v?.vendor?.fullName || "";

const getGST = (v) => v?.vendor?.gstNumber || "";

const getAddressStrict = (v) =>
  typeof v?.vendor?.address === "string" ? v.vendor.address : "";

const getVendorId = (v) => String(v?._id || v?.vendor?._id || v?.userId?._id || "");
const getVendorName = (v) => v.fullName ?? v.userId?.fullName ?? v.vendor?.userId?.fullName ?? "";
const getVendorEmail = (v) => v.email ?? v.userId?.email ?? v.vendor?.userId?.email ?? "";
const getVendorMobile = (v) => v.mobile ?? v.userId?.mobile ?? v.vendor?.userId?.mobile ?? "";
const getVendorType = (v) => v.vendorType || v.vendor?.vendorType || v.profile?.vendorType || "Vendor";
const getProductInterest = (v) => {
  const value = v.productInterest || v.vendor?.productInterest || v.profile?.productInterest || "";
  return Array.isArray(value) ? value.join(", ") : value;
};
const getTurnover = (v) => v.turnover || v.vendor?.turnover || v.profile?.turnover || "";
const getCertificateStatus = (v) =>
  v.certificateStatus ?? v.vendor?.certificateStatus ?? (v.certificateIssued ? "generated" : "pending");

const StatusPills = ({ approved, certificateStatus, profileLocked }) => (
  <div className="vm-status-stack">
    <span className={`vm-status-pill ${approved ? "approved" : "pending"}`}>
      {approved ? "Approved" : "Pending"}
    </span>
    <span className={`vm-mini-pill ${certificateStatus === "generated" || certificateStatus === "issued" ? "verified" : "waiting"}`}>
      Cert: {certificateStatus || "pending"}
    </span>
    <span className={`vm-mini-pill ${profileLocked ? "waiting" : "neutral"}`}>
      Edit: {profileLocked ? "locked" : "open"}
    </span>
  </div>
);

/* CSV export of visible vendors */
const exportCSV = (rows) => {
  if (!rows?.length) return;
  const headers = [
    "Name",
    "Email",
    "Mobile",
    "Company",
    "GST",
    "Approved",
    "CertificateStatus",
    "Address",
    "CreatedAt",
    "UpdatedAt",
    "RawID",
  ];
  const csv = [
    headers.join(","),
    ...rows.map((v) => {
      const name =
        v.fullName ?? v.userId?.fullName ?? v.vendor?.userId?.fullName ?? "";
      const email = v.email ?? v.userId?.email ?? v.vendor?.userId?.email ?? "";
      const mobile =
        v.mobile ?? v.userId?.mobile ?? v.vendor?.userId?.mobile ?? "";
      const company = getCompany(v);
      const gst = getGST(v);
      const approved = (v.approved ?? v.vendor?.approved) ?? "";
      const certificateStatus =
        v.certificateStatus ?? v.vendor?.certificateStatus ?? "";
      const address = getAddressStrict(v).replaceAll(",", " ");
      const createdAt = v.createdAt || v.vendor?.createdAt || "";
      const updatedAt = v.updatedAt || v.vendor?.updatedAt || "";
      const id = v._id || v.vendor?._id || "";
      const row = [
        name,
        email,
        mobile,
        company,
        gst,
        approved,
        certificateStatus,
        address,
        createdAt ? new Date(createdAt).toISOString() : "",
        updatedAt ? new Date(updatedAt).toISOString() : "",
        id,
      ];
      return row.map((x) => `"${String(x ?? "").replaceAll(`"`, `""`)}"`).join(",");
    }),
  ].join("\n");

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `assigned_vendors_${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

/* ---------- main component ---------- */
const AssignedVendors = () => {
  const navigate = useNavigate();
  const [vendors, setVendors] = useState([]);
  const [perms, setPerms] = useState([]);
  const [date, setDate] = useState("");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);
  const [grantingId, setGrantingId] = useState("");

  const token = typeof window !== "undefined" ? localStorage.getItem("token") : null;
  const auth = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    fetchVendors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);

  useEffect(() => {
    const fetchPerms = async () => {
      try {
        const res = await API.get("/auth/me", { headers: auth });
        const nextPerms = res.data?.employeeRole?.permissions || [];
        setPerms(Array.isArray(nextPerms) ? nextPerms : []);
      } catch {
        setPerms([]);
      }
    };
    fetchPerms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchVendors = async () => {
    setLoading(true);
    setError("");
    try {
      let url = "/employee/my-vendors?full=1";
      if (date) url += `&date=${date}`;
      const res = await API.get(url, { headers: auth });
      setVendors(Array.isArray(res.data) ? res.data : res.data?.items || []);
    } catch (err) {
      console.error("Error fetching vendors:", err);
      setError(
        err?.response?.data?.message || "Failed to load vendors. Please try again."
      );
      setVendors([]);
    } finally {
      setLoading(false);
    }
  };

  /* ---------- derived stats ---------- */
  const stats = useMemo(() => {
    const total = vendors.length;
    const approved = vendors.filter((v) => (v.approved ?? v.vendor?.approved) === true).length;
    const certIssued = vendors.filter((v) => {
      const st = v.certificateStatus ?? v.vendor?.certificateStatus;
      return st === "generated" || st === "issued" || v.certificateIssued === true;
    }).length;
    return { total, approved, certIssued };
  }, [vendors]);

  const filtered = useMemo(() => {
    if (!q.trim()) return vendors;
    const s = q.trim().toLowerCase();
    return vendors.filter((v) => {
      const fields = [
        getVendorName(v),
        getVendorEmail(v),
        getVendorMobile(v),
        getCompany(v),
        getGST(v),
        getAddressStrict(v),
        getVendorType(v),
        getProductInterest(v),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return fields.includes(s);
    });
  }, [vendors, q]);

  const canGrantEditAccess = perms.includes("vendor-master:edit");

  const grantEditAccess = async (vendorUserId) => {
    if (!vendorUserId) return;
    try {
      setGrantingId(String(vendorUserId));
      setError("");
      const res = await API.patch(`/employee/vendors/${vendorUserId}/profile-edit-access`, {}, { headers: auth });
      setVendors((prev) =>
        prev.map((row) =>
          String(row._id) === String(vendorUserId)
            ? {
                ...row,
                vendor: {
                  ...(row.vendor || {}),
                  profileLocked: false,
                  profileEditUnlocked: true,
                },
              }
            : row
        )
      );
      window.alert(res?.data?.message || "Edit access granted");
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to grant edit access");
    } finally {
      setGrantingId("");
    }
  };

  return (
    <div className="vm-page">
      <div className="vm-container">
        <div className="vm-topbar">
          <div className="vm-title-wrap">
            <div className="vm-logo">AV</div>
            <div>
              <div className="vm-title">Assigned Vendors</div>
              <div className="vm-subtitle vm-subtitle-normal">Admin-style vendor cards for vendors assigned to you</div>
            </div>
          </div>
          <div className="vm-search vm-search-top d-none d-md-block">
            <span className="material-symbols-outlined">search</span>
            <input
              type="search"
              placeholder="Search vendors by name, company, GST, mobile..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
          <div className="vm-actions">
            <button className="vm-toolbar-btn vm-toolbar-btn-primary" onClick={() => exportCSV(filtered)} disabled={!filtered.length}>
              <span className="material-symbols-outlined">download</span>
              <span>Export CSV</span>
            </button>
            <button className="vm-toolbar-btn" onClick={fetchVendors}>
              <span className="material-symbols-outlined">refresh</span>
              <span>Refresh</span>
            </button>
          </div>
        </div>

        <div className="vm-toolbar">
          <div className="vm-tabs">
            <button className="vm-tab active" type="button">
              <span>Total Assigned</span>
              <span className="vm-tab-badge blue">{stats.total}</span>
            </button>
            <button className="vm-tab" type="button">
              <span>Approved</span>
              <span className="vm-tab-badge green">{stats.approved}</span>
            </button>
            <button className="vm-tab" type="button">
              <span>Certificates</span>
              <span className="vm-tab-badge amber">{stats.certIssued}</span>
            </button>
          </div>
          <div className="vm-filter-row">
            <div className="vm-filter-stack">
              <div className="d-flex gap-2 flex-wrap">
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="form-control"
                  style={{ width: 170, borderRadius: 10, fontSize: 12 }}
                />
                <Button size="sm" variant="outline-primary" onClick={() => setDate(new Date().toISOString().split("T")[0])}>
                  Today
                </Button>
                <Button size="sm" variant="outline-secondary" onClick={() => setDate("")}>
                  Clear
                </Button>
              </div>
              <div className="vm-search d-md-none">
                <span className="material-symbols-outlined">search</span>
                <input
                  type="search"
                  placeholder="Search vendors..."
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>

      {/* Loading / Error */}
      {loading && (
        <div className="text-center my-5">
          <Spinner animation="border" />
          <div className="mt-2 text-muted">Loading vendors...</div>
        </div>
      )}
      {error && <div className="alert alert-danger">{error}</div>}

      {/* Cards */}
      {!loading && !error && filtered.length > 0 ? (
        <div className="vm-content">
          <Row xs={1} md={2} xl={3} className="g-3">
            {filtered.map((v, i) => {
                const vendorId = getVendorId(v);
                const name = getVendorName(v);
                const email = getVendorEmail(v);
                const mobile = getVendorMobile(v);
                const company = getCompany(v);
                const gst = getGST(v);
                const address = getAddressStrict(v);
                const approved = v.approved ?? v.vendor?.approved;
                const cert = getCertificateStatus(v);
                const profileLocked = !!v.vendor?.profileLocked;
                const createdAt = v.createdAt ?? v.vendor?.createdAt;
                const updatedAt = v.updatedAt ?? v.vendor?.updatedAt;
                const vendorType = getVendorType(v);
                const productInterest = getProductInterest(v);
                const turnover = getTurnover(v);

                return (
                  <Col key={vendorId || i}>
                    <div className="vm-entity-grid-card">
                      <div className="vm-entity-head">
                        <div className="vm-supplier-head">
                          <div className={`vm-avatar vm-avatar-${getAvatarTone(vendorId || company || name)}`}>
                            {getInitials(company || name)}
                          </div>
                          <div className="vm-supplier-copy">
                            <div className="vm-person-name">{orDash(company || name)}</div>
                            <div className="vm-company-name">{orDash(name)}</div>
                            <div className="vm-code-chip">{orDash(vendorType)}</div>
                          </div>
                        </div>
                        <StatusPills approved={approved === true} certificateStatus={cert} profileLocked={profileLocked} />
                      </div>
                      <div className="vm-kv-grid">
                        <div className="vm-kv">
                          <span className="vm-k"><span className="material-symbols-outlined">smartphone</span>Mobile</span>
                          <span className="vm-v">{phoneLink(mobile) || "—"}</span>
                        </div>
                        <div className="vm-kv">
                          <span className="vm-k"><span className="material-symbols-outlined">mail</span>Email</span>
                          <span className="vm-v">{mailLink(email) || "—"}</span>
                        </div>
                        <div className="vm-kv">
                          <span className="vm-k"><span className="material-symbols-outlined">badge</span>GST Number</span>
                          <span className="vm-v vm-v-mono">{orDash(gst)}</span>
                        </div>
                        <div className="vm-kv">
                          <span className="vm-k"><span className="material-symbols-outlined">inventory_2</span>Interest</span>
                          <span className="vm-v">{orDash(productInterest)}</span>
                        </div>
                        <div className="vm-kv">
                          <span className="vm-k"><span className="material-symbols-outlined">payments</span>Turnover</span>
                          <span className="vm-v">{orDash(turnover)}</span>
                        </div>
                        <div className="vm-kv vm-kv-full">
                          <span className="vm-k"><span className="material-symbols-outlined">location_on</span>Address</span>
                          <span className="vm-v">{orDash(address)}</span>
                        </div>
                      </div>
                      <div className="vm-meta-row">
                        <div className="vm-meta-chip">
                          <span className="material-symbols-outlined">calendar_today</span>
                          <span>Created: {fmtDate(createdAt)}</span>
                        </div>
                        <div className="vm-meta-chip">
                          <span className="material-symbols-outlined">update</span>
                          <span>Updated: {fmtDate(updatedAt)}</span>
                        </div>
                      </div>
                      <div className="vm-po-grid">
                        <div className="vm-po-cell">
                          <span className="vm-k"><span className="material-symbols-outlined">assignment_ind</span>Assigned Access</span>
                          <span className="vm-v">Visible to current employee</span>
                        </div>
                        <div className="vm-po-cell">
                          <span className="vm-k"><span className="material-symbols-outlined">verified_user</span>Approval</span>
                          <span className="vm-v">{approved === true ? "Approved" : "Pending"}</span>
                        </div>
                      </div>
                      <div className="vm-actions-wrap">
                        <Button size="sm" className="vm-action-btn vm-action-neutral" onClick={() => navigate(`/employee/my-vendors/${vendorId}`)}>
                          <span className="material-symbols-outlined">badge</span>
                          <span>Full Profile</span>
                        </Button>
                        <Button
                          size="sm"
                          className="vm-action-btn vm-action-neutral"
                          onClick={() => setSelected({ name, email, mobile, company, gst, profileLocked, address, cert, approved })}
                        >
                          <span className="material-symbols-outlined">visibility</span>
                          <span>Quick View</span>
                        </Button>
                        {canGrantEditAccess && profileLocked ? (
                          <Button
                            size="sm"
                            className="vm-action-btn vm-action-amber"
                            disabled={grantingId === String(vendorId)}
                            onClick={() => grantEditAccess(vendorId)}
                          >
                            <span className="material-symbols-outlined">edit</span>
                            <span>{grantingId === String(vendorId) ? "Granting..." : "Give Edit Access"}</span>
                          </Button>
                        ) : null}
                        {canGrantEditAccess && !profileLocked ? (
                          <Badge bg="success" className="d-inline-flex align-items-center justify-content-center rounded-pill">
                            Edit Enabled
                          </Badge>
                        ) : null}
                      </div>
                    </div>
                  </Col>
                );
              })}
          </Row>
        </div>
      ) : (
        !loading &&
        !error && (
          <p className="text-muted">
            No vendors found{q ? " for this search." : date ? " for this date." : "."}
          </p>
        )
      )}

      {/* Details Modal — only 4 fields now */}
      <Modal show={!!selected} onHide={() => setSelected(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Vendor Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selected && (
            <div className="px-1">
              <div className="mb-2">
                <strong>Name:</strong> {selected.name || ""}
              </div>
              <div className="mb-2">
                <strong>Email:</strong> {selected.email || ""}
              </div>
              <div className="mb-2">
                <strong>Mobile:</strong> {selected.mobile || ""}
              </div>
              <div className="mb-2">
                <strong>Company Name:</strong> {selected.company || ""}
                {selected.gst ? ` • ${selected.gst}` : ""}
              </div>
              <div className="mb-2">
                <strong>Address:</strong> {selected.address || ""}
              </div>
              <div className="mb-2">
                <strong>Approval:</strong> {selected.approved ? "Approved" : "Pending"}
              </div>
              <div className="mb-2">
                <strong>Certificate:</strong> {selected.cert || "pending"}
              </div>
              <div className="mb-2">
                <strong>Edit Lock:</strong> {selected.profileLocked ? "Locked" : "Edit Enabled"}
              </div>
            </div>
          )}
        </Modal.Body>
      </Modal>
      </div>
    </div>
  );
};

export default AssignedVendors;
